import {
  useGetFrontRichiesteIdGestore,
  useGetFrontRichiesteIdModuliDeleghe,
  useGetFrontRichiesteIdProcuratore,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type DatiTitolare } from '@framework/api-client/api/inquiries-compile/schemas/datiTitolare.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import {
  DelegationLogic,
  type IDelegationData,
} from '@framework/frontoffice-submission/module/delegation/DelegationLogic.ts';
import { useMemo } from 'react';

function getApplicantName(applicant: DatiTitolare) {
  if (!applicant.nome && !applicant.cognome)
    return applicant.codiceFiscale ?? '';
  return `${applicant.nome} ${applicant.cognome}`;
}

export function useDelegationData(params: {
  submissionId: string | null | undefined;
  applicant: DatiTitolare[] | null | undefined;
  modules: RichiestaModuloDTO[];
}) {
  const { submissionId, modules, applicant } = params;
  // Deleghe
  const {
    data,
    isLoading: delegationLoading,
    error: delegationError,
  } = useGetFrontRichiesteIdModuliDeleghe(submissionId ?? '<unknown>');
  // gestore
  const {
    data: manager,
    isLoading: managerLoading,
    error: managerError,
  } = useGetFrontRichiesteIdGestore(submissionId ?? '<unknown>');
  // procuratore
  const {
    data: attorney,
    isLoading: attorneyLoading,
    error: attorneyError,
  } = useGetFrontRichiesteIdProcuratore(submissionId ?? '<unknown>');

  const delegationData = useMemo<IDelegationData>(() => {
    const moduleDelegation = DelegationLogic.getModuleDelegations({
      modules,
      data: data ?? [],
    });

    return {
      // Titolare
      applicant:
        applicant?.map(applicant => ({
          fiscalCode: applicant.codiceFiscale ?? '',
          name: getApplicantName(applicant),
          // Solo il titolare principale può inoltrare la pratica
          canSubmit: applicant.flTitolarePrincipale ?? false,
          isMain: applicant.flTitolarePrincipale ?? false,
          data: applicant,
        })) ?? [],
      // Gestore
      manager: manager?.codiceFiscale
        ? {
            fiscalCode: manager?.codiceFiscale ?? '',
            name: DelegationLogic.getNameWithDefault(manager),
          }
        : null,
      // Procuratore
      attorney: attorney?.datiProcura
        ? {
            fiscalCode: attorney?.datiProcura?.codiceFiscale ?? '',
            name:
              attorney.datiProcura.nome ??
              attorney?.datiProcura?.codiceFiscale ??
              '',
            surname: attorney.datiProcura.cognome,
            email: attorney.datiProcura.email,
            canSubmit: attorney.datiProcura?.permetteInoltro ?? false,
            uploadedFileId: attorney.datiProcura?.fileProcura,
            data: attorney.datiProcura,
          }
        : null,
      // Deleghe visibilità/firma/visibilità
      moduleDelegation,
    };
  }, [modules, data, manager, attorney, applicant]);

  return {
    delegationData,
    isLoading: delegationLoading || managerLoading || attorneyLoading,
    error: delegationError ?? managerError ?? attorneyError,
  };
}
